import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn";

interface Props {
  label: string;
  icon?: React.ReactNode;
  /** Short value shown at the right of the header (e.g. the material name). */
  summary?: React.ReactNode;
  /** Turns the header into a disclosure toggle. Phones use this so the
   *  inspector opens on the essentials (size) with details one tap away. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  testId?: string;
  children: React.ReactNode;
}

/** A titled inspector block with an optional expand/collapse header. */
export function InspectorSection({
  label,
  icon,
  summary,
  collapsible = false,
  defaultOpen = true,
  testId,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  const header = (
    <>
      {icon}
      <span>{label}</span>
      {summary != null && (
        <span className="ml-auto truncate text-xs font-medium normal-case tracking-normal text-ink-700">
          {summary}
        </span>
      )}
      {collapsible && (
        <ChevronDown
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-ink-400 transition-transform",
            summary == null && "ml-auto",
            isOpen && "rotate-180"
          )}
        />
      )}
    </>
  );
  const headerClass =
    "flex w-full items-center gap-1.5 px-4 text-caption font-semibold uppercase tracking-wide text-ink-500";

  return (
    <section className="border-t border-ink-100" data-testid={testId}>
      {collapsible ? (
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setOpen((v) => !v)}
          className={cn(headerClass, "min-h-12 text-left active:bg-ink-50")}
        >
          {header}
        </button>
      ) : (
        <div className={cn(headerClass, "pt-3 pb-2")}>{header}</div>
      )}
      {isOpen && <div className="px-4 pb-3">{children}</div>}
    </section>
  );
}
